// frontend/src/components/staff/ScheduleAuditDrawer.js
//
// Workforce — SCHED-SLICE1-F per-shift audit trail drawer.
//
// In-context side drawer that opens from the Edit Shift modal. Renders the
// audit history for a single shift, newest-first, with action-color
// left-accent stripes and before/after diff rendering.
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §7.1 + §7.3
// Story:       docs/backlog/SCHED-SLICE1-F-audit-trail-viewer-ui.md
//
// Mirrors the ChangeHistoryDrawer (TIME-010) pattern used in TimesheetReview
// per Roderick directive "format consistent with existing AuditLog patterns"
// (visual spec §7.3). Reuse, not invention.
//
// Read-only by design — no edit/delete affordances per Scenario 9.

import React from "react";
import {
  computeDiff,
  formatAuditTimestamp,
  formatShiftIdentifier,
  getActionColorKey,
  getActionVerb,
  resolveActorName,
  sortAuditRowsNewestFirst,
} from "./scheduleAuditUtils";
import "./ScheduleAuditDrawer.css";

/**
 * Per-shift audit drawer.
 *
 * Props:
 *   - open           : boolean — open/closed state (caller-controlled)
 *   - shift          : { _id, startTime, endTime, ... } — the shift being audited
 *   - rows           : Array<AuditLog> — pre-fetched audit rows for this shift
 *   - isLoading      : boolean — show loading state
 *   - error          : Error|null — render an error message if present
 *   - staffNameById  : Map<string, string> — for resolving staffProfileId in diffs
 *   - userNameById   : Map<string, string> — for resolving createdBy actor names
 *   - onClose        : () => void
 *   - rowAnchor      : string|null — auditLog._id to scroll into view on open
 */
const ScheduleAuditDrawer = ({
  open,
  shift,
  rows,
  isLoading,
  error,
  staffNameById,
  userNameById,
  onClose,
  rowAnchor,
}) => {
  const drawerRef = React.useRef(null);
  const rowRefs = React.useRef(new Map());

  // Scroll a specific audit row into view when the drawer opens with a
  // rowAnchor (used by per-week page row click -> Edit modal -> drawer flow).
  React.useEffect(() => {
    if (!open || !rowAnchor) return;
    const node = rowRefs.current.get(rowAnchor);
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [open, rowAnchor, rows]);

  // Esc key closes drawer (a11y).
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && typeof onClose === "function") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const safeRows = sortAuditRowsNewestFirst(rows);
  const ctx = { staffNameById, userNameById };

  return (
    <>
      <div
        className="schedule-audit-drawer__backdrop"
        onClick={onClose}
        aria-hidden="true"
        data-testid="schedule-audit-drawer-backdrop"
      />
      <aside
        ref={drawerRef}
        className="schedule-audit-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-audit-drawer-heading"
        data-testid="schedule-audit-drawer"
      >
        <header className="schedule-audit-drawer__header">
          <div>
            <h2
              id="schedule-audit-drawer-heading"
              className="schedule-audit-drawer__heading"
            >
              Shift history
            </h2>
            <p className="schedule-audit-drawer__subline">
              {formatShiftIdentifier(shift)}
            </p>
          </div>
          <button
            type="button"
            className="schedule-audit-drawer__close"
            onClick={onClose}
            aria-label="Close shift history"
            data-testid="schedule-audit-drawer-close"
          >
            &times;
          </button>
        </header>

        <div className="schedule-audit-drawer__body">
          {isLoading && (
            <p className="schedule-audit-drawer__loading">
              Loading audit history...
            </p>
          )}

          {error && !isLoading && (
            <p className="schedule-audit-drawer__error" role="alert">
              Unable to load audit history. Try again.
            </p>
          )}

          {!isLoading && !error && safeRows.length === 0 && (
            <p
              className="schedule-audit-drawer__empty"
              data-testid="schedule-audit-drawer-empty"
            >
              No audit entries for this shift yet.
            </p>
          )}

          {!isLoading && !error && safeRows.length > 0 && (
            <ul className="schedule-audit-drawer__list">
              {safeRows.map((row) => (
                <AuditRow
                  key={row._id || row.id || row.createdAt}
                  row={row}
                  ctx={ctx}
                  refSetter={(node) => {
                    if (node && (row._id || row.id)) {
                      rowRefs.current.set(row._id || row.id, node);
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
};

/**
 * Single audit row — used by both the drawer and the per-week page.
 *
 * Renders:
 *   - left-accent stripe per action color
 *   - timestamp + actor + action verb on row 1
 *   - diff/detail on row 2
 *
 * Pure presentation — no fetching, no state.
 */
export const AuditRow = ({ row, ctx, onClick, refSetter, clickable }) => {
  if (!row) return null;
  const colorKey = getActionColorKey(row.action);
  const verb = getActionVerb(row.action);
  const actor = resolveActorName(row, ctx);
  const timestamp = formatAuditTimestamp(row.createdAt || row.timestamp);
  const metadata = row.metadata || {};

  let detail = null;
  if (row.action === "shift_edited") {
    const diffs = computeDiff(metadata.before, metadata.after, ctx);
    if (diffs.length === 0) {
      detail = (
        <span className="schedule-audit-row__detail-muted">
          (no field-level changes recorded)
        </span>
      );
    } else {
      detail = (
        <ul
          className="schedule-audit-row__diff-list"
          data-testid="schedule-audit-row-diff-list"
        >
          {diffs.map((d) => (
            <li key={d.key} className="schedule-audit-row__diff-line">
              <span className="schedule-audit-row__diff-label">{d.label}:</span>{" "}
              <span className="schedule-audit-row__diff-old">{d.oldDisplay}</span>{" "}
              <span aria-hidden="true">&rarr;</span>{" "}
              <span className="schedule-audit-row__diff-new">{d.newDisplay}</span>
            </li>
          ))}
        </ul>
      );
    }
  } else if (row.action === "shift_created" || row.action === "shift_published") {
    const startLabel = metadata.startTime
      ? formatAuditTimestamp(metadata.startTime)
      : null;
    const staff =
      ctx && ctx.staffNameById && metadata.staffProfileId
        ? ctx.staffNameById.get(metadata.staffProfileId)
        : null;
    detail = (
      <span className="schedule-audit-row__detail">
        {staff ? `${staff}` : null}
        {staff && startLabel ? " " : null}
        {startLabel ? `(${startLabel})` : null}
      </span>
    );
  } else if (row.action === "shift_cancelled") {
    detail = (
      <span className="schedule-audit-row__detail">
        {metadata.reason ? `Reason: ${metadata.reason}` : "Cancelled."}
      </span>
    );
  } else if (row.action === "shift_deleted") {
    detail = (
      <span className="schedule-audit-row__detail">Draft shift deleted.</span>
    );
  } else if (row.action === "schedule_published") {
    const count = Array.isArray(metadata.shiftIds) ? metadata.shiftIds.length : 0;
    detail = (
      <span className="schedule-audit-row__detail">
        {count > 0 ? `Published ${count} shift${count === 1 ? "" : "s"}` : "Bulk publish"}
      </span>
    );
  }

  const Tag = clickable ? "button" : "li";
  const interactiveProps = clickable
    ? {
        type: "button",
        onClick: () => typeof onClick === "function" && onClick(row),
        className: `schedule-audit-row schedule-audit-row--accent-${colorKey} schedule-audit-row--clickable`,
        tabIndex: 0,
      }
    : {
        className: `schedule-audit-row schedule-audit-row--accent-${colorKey}`,
      };

  return (
    <Tag
      {...interactiveProps}
      ref={refSetter}
      data-testid={`schedule-audit-row-${row.action}`}
      data-audit-id={row._id || row.id || ""}
    >
      <span
        aria-hidden="true"
        className={`schedule-audit-row__stripe schedule-audit-row__stripe--${colorKey}`}
      />
      <div className="schedule-audit-row__content">
        <div className="schedule-audit-row__line-1">
          <span className="schedule-audit-row__timestamp">{timestamp}</span>
          <span className="schedule-audit-row__actor-verb">
            <span className="schedule-audit-row__actor">{actor}</span>{" "}
            <span className="schedule-audit-row__verb">{verb}</span>
          </span>
        </div>
        {detail && <div className="schedule-audit-row__line-2">{detail}</div>}
      </div>
    </Tag>
  );
};

export default ScheduleAuditDrawer;
